// Pure calculation engine for the Claim Calculator.
// Input: the answers object from the wizard.
// Output: { low, likely, high, breakdown[] }
import {
  RANGE_MULTIPLIER,
  LIKELY_POSITION,
  ROOF_PER_SQUARE,
  ROOF_HOUSE_TO_ROOF_RATIO,
  ROOF_ADDONS,
  INTERIOR_PER_ROOM,
  INTERIOR_FLOORING_PER_SQFT,
  INTERIOR_MOLD_REMEDIATION,
  WINDOW_PER_UNIT,
  SIDING_PER_SQFT,
  CABINET_PER_LINEAR_FOOT,
  COUNTERTOP_PER_SQFT,
  SCREEN_ENCLOSURE_PER_SQFT,
  SCREEN_ENCLOSURE_DOOR,
  AC_SYSTEM,
  AC_DUCTWORK,
  GARAGE_DOOR,
  FENCE_PER_LINEAR_FOOT,
  FENCE_GATE,
  CONTENTS_RECOVERY_MULTIPLIER,
  CONTENTS_MINIMUM,
  UNKNOWN_DEFAULTS,
} from "./calculatorRates";

const n = (v) => Number(v) || 0;
// Helper: read a numeric field, swap to fallback if user checked "I don't know"
const readNum = (a, key, fallbackKey) => {
  if (a[`${key}Unknown`]) return UNKNOWN_DEFAULTS[fallbackKey];
  return n(a[key]);
};

const calcRoof = (a) => {
  const houseSqft = readNum(a, "houseSqft", "houseSqft");
  if (houseSqft <= 0) return 0;
  const roofSqft = houseSqft * ROOF_HOUSE_TO_ROOF_RATIO;
  const squares = roofSqft / 100;
  const base =
    (ROOF_PER_SQUARE[a.material] || ROOF_PER_SQUARE.asphalt) * squares;
  let addons = 0;
  const gutter = readNum(a, "gutterFeet", "gutterFeet");
  if (gutter > 0) addons += ROOF_ADDONS.gutterReplace * gutter;
  return base + addons;
};

const calcInterior = (a) => {
  const rooms = readNum(a, "roomsAffected", "roomsAffected");
  if (rooms <= 0 && !a.moldLevel) return 0;
  const perRoom =
    INTERIOR_PER_ROOM[a.severity] || INTERIOR_PER_ROOM.moderate;
  let total = perRoom * rooms;
  const floorSqft = readNum(a, "floorSqft", "floorSqft");
  if (floorSqft > 0 && a.floorType) {
    total += (INTERIOR_FLOORING_PER_SQFT[a.floorType] || 0) * floorSqft;
  }
  if (a.moldLevel && a.moldLevel !== "none") {
    total += INTERIOR_MOLD_REMEDIATION[a.moldLevel] || 0;
  }
  return total;
};

const calcWindows = (a) => {
  // Simplified: single total + dominant type
  const count = readNum(a, "totalCount", "windowCount");
  if (count <= 0) return 0;
  const rate =
    WINDOW_PER_UNIT[a.dominantType] || WINDOW_PER_UNIT.standard;
  return rate * count;
};

const calcSiding = (a) => {
  const sqft = readNum(a, "sqft", "sidingSqft");
  if (sqft <= 0) return 0;
  return (SIDING_PER_SQFT[a.material] || SIDING_PER_SQFT.vinyl) * sqft;
};

const calcCabinets = (a) => {
  const lf = readNum(a, "linearFeet", "cabinetLinearFeet");
  const counterSqft = readNum(a, "counterSqft", "counterSqft");
  let total = 0;
  if (lf > 0) {
    total +=
      (CABINET_PER_LINEAR_FOOT[a.grade] || CABINET_PER_LINEAR_FOOT.midGrade) *
      lf;
  }
  if (counterSqft > 0) {
    total +=
      (COUNTERTOP_PER_SQFT[a.counterMaterial] || COUNTERTOP_PER_SQFT.granite) *
      counterSqft;
  }
  return total;
};

const calcScreenEnclosure = (a) => {
  const sqft = readNum(a, "sqft", "screenEnclosureSqft");
  if (sqft <= 0) return 0;
  const base =
    (SCREEN_ENCLOSURE_PER_SQFT[a.scope] || SCREEN_ENCLOSURE_PER_SQFT.rescreen) *
    sqft;
  const doors = readNum(a, "doorsNeeded", "doorsNeeded") * SCREEN_ENCLOSURE_DOOR;
  return base + doors;
};

const calcAC = (a) => {
  let total = 0;
  if (a.system && a.system !== "none") {
    total += AC_SYSTEM[a.system] || 0;
  }
  if (a.ductwork && a.ductwork !== "none") {
    total += AC_DUCTWORK[a.ductwork] || 0;
  }
  return total;
};

const calcGarage = (a) => {
  if (!a.type || a.type === "none") return 0;
  let total = GARAGE_DOOR[a.type] || 0;
  if (a.openerDamaged) total += GARAGE_DOOR.openerAdd;
  return total;
};

const calcFence = (a) => {
  const lf = readNum(a, "linearFeet", "fenceLinearFeet");
  if (lf <= 0) return 0;
  const base =
    (FENCE_PER_LINEAR_FOOT[a.material] || FENCE_PER_LINEAR_FOOT.wood) * lf;
  const gates = readNum(a, "gates", "gates") * FENCE_GATE;
  return base + gates;
};

const calcContents = (a) => {
  const value = readNum(a, "estimatedValue", "contentsValue");
  if (value <= 0) return 0;
  const adjusted = value * CONTENTS_RECOVERY_MULTIPLIER;
  return Math.max(adjusted, CONTENTS_MINIMUM);
};

const SECTIONS = [
  { key: "roof", label: "Roof Damage", fn: calcRoof },
  { key: "interior", label: "Interior Damage", fn: calcInterior },
  { key: "windows", label: "Window Damage", fn: calcWindows },
  { key: "siding", label: "Siding Damage", fn: calcSiding },
  { key: "cabinets", label: "Cabinet & Countertop Damage", fn: calcCabinets },
  {
    key: "screenEnclosure",
    label: "Screen Enclosure / Pool Cage",
    fn: calcScreenEnclosure,
  },
  { key: "ac", label: "A/C & HVAC Damage", fn: calcAC },
  { key: "garage", label: "Garage Door Damage", fn: calcGarage },
  { key: "fence", label: "Fence Damage", fn: calcFence },
  { key: "contents", label: "Contents / Personal Property", fn: calcContents },
];

export const calculateEstimate = (answers) => {
  const breakdown = [];
  let lowSubtotal = 0;
  SECTIONS.forEach((sec) => {
    if (!answers.selected?.[sec.key]) return;
    const value = sec.fn(answers[sec.key] || {});
    if (value > 0) {
      breakdown.push({ label: sec.label, value });
      lowSubtotal += value;
    }
  });
  const high = lowSubtotal * RANGE_MULTIPLIER;
  const likely = lowSubtotal + (high - lowSubtotal) * LIKELY_POSITION;
  return {
    low: Math.round(lowSubtotal),
    likely: Math.round(likely),
    high: Math.round(high),
    breakdown,
  };
};

export const SECTION_DEFS = SECTIONS;
